import { computed, defineComponent, onMounted, reactive, ref, watch, } from 'vue';
import { storeToRefs } from 'pinia';
import validator from 'validator';
import { Button, Checkbox, Input } from '@yellowcard/components-library-vue3';
import TransactHeader from '@/components/transact/TransactHeader.vue';
import LoaderComponent from '@/components/general/LoaderComponent.vue';
import InlineMessageAlert from '@/components/general/InlineMessageAlert.vue';
import { numberFormat } from '@/utils/helpers';
import router from '@/router';
import { useAuthStore } from '@/store/modules/auth';
import { useInterceptorStore } from '@/store/modules/interceptor';
import { useKycStore } from '@/store/modules/kyc';
import { usePaymentStore } from '@/store/modules/payment';
import useTransactionsStore from '@/store/modules/transactions';
import { KycTypes } from '@/types/kyc';
import { TransactTypes } from '@/types/transact';
export default defineComponent({
    name: 'WalletAddress',
    components: {
        Button,
        Checkbox,
        InlineMessageAlert,
        Input,
        LoaderComponent,
        TransactHeader,
    },
    setup() {
        const transactionsStore = useTransactionsStore();
        const { selectedCrypto, selectedCryptoAmount, walletAddress, currentStep, walletTagRequired, shouldTriggerTravelRule, loading: transactionLoading, error: transactionError, } = storeToRefs(transactionsStore);
        const paymentStore = usePaymentStore();
        const authStore = useAuthStore();
        const { isLoggedIn, travelRuleConfig } = storeToRefs(authStore);
        const kycStore = useKycStore();
        const { kycStatus } = storeToRefs(kycStore);
        const interceptorStore = useInterceptorStore();
        const { loading: interceptorLoading, error: interceptorError } = storeToRefs(interceptorStore);
        const error = ref(undefined);
        const buttonDisabled = ref(true);
        const tagText = `${selectedCrypto.value.networkType} tag/memo`;
        const travelRuleFields = ref([]);
        const travelRuleInfo = reactive({});
        function initializeTravelRuleInfo() {
            travelRuleFields.value = JSON.parse(travelRuleConfig.value?.dataFields || '[]');
            travelRuleFields.value.forEach(field => {
                if (field.fieldType === 'string') {
                    travelRuleInfo[field.payloadName] = '';
                }
                else if (field.fieldType === 'select') {
                    travelRuleInfo[field.payloadName] = [];
                }
            });
        }
        onMounted(() => {
            transactionError.value = undefined;
            initializeTravelRuleInfo();
        });
        const rules = computed(() => ({
            travelRuleString: (value, label, required) => {
                if (!required)
                    return true;
                if (validator.isEmpty(value))
                    return `${label} is required`;
                return true;
            },
            travelRuleSelect: (value, label, required) => {
                if (!required)
                    return true;
                if (!value.length)
                    return `${label} is required`;
                return true;
            },
        }));
        const checkIfButtonIsDisabled = (inputWalletAddress, tag) => {
            if (shouldTriggerTravelRule.value) {
                // eslint-disable-next-line
                for (const field of travelRuleFields.value) {
                    // eslint-disable-next-line
                    if (!field.required)
                        continue;
                    if (field.fieldType === 'select') {
                        const selectInvalid = rules.value.travelRuleSelect(travelRuleInfo[field.payloadName], field.displayName, field.required) !== true;
                        if (selectInvalid) {
                            return true;
                        }
                    }
                    if (field.fieldType === 'string') {
                        const stringInvalid = rules.value.travelRuleString(travelRuleInfo[field.payloadName], field.displayName, field.required) !== true;
                        if (stringInvalid) {
                            return true;
                        }
                    }
                }
            }
            if (walletTagRequired.value) {
                return !(!validator.isEmpty(inputWalletAddress) && !validator.isEmpty(tag));
            }
            return validator.isEmpty(inputWalletAddress);
        };
        watch(travelRuleInfo, _ => {
            buttonDisabled.value = checkIfButtonIsDisabled(walletAddress.value.address, walletAddress.value.tag);
        });
        if (walletAddress.value.address) {
            buttonDisabled.value = checkIfButtonIsDisabled(walletAddress.value.address, walletAddress.value.tag);
        }
        const loading = ref(false);
        const showHome = computed(() => {
            if (isLoggedIn.value) {
                return kycStatus.value === KycTypes.KycStatus.APPROVED;
            }
            return false;
        });
        const network = computed(() => {
            const { standard: standard_, token: token_, networkType: networkType_, } = selectedCrypto.value;
            if (networkType_)
                return networkType_.toLocaleUpperCase();
            const tokens = TransactTypes.TokenNetworkMapping[token_] || [];
            if (standard_) {
                return (tokens.find(_token => _token.includes(standard_.toLocaleUpperCase())) || standard_);
            }
            return tokens[0];
        });
        watch(network, () => {
            walletTagRequired.value = [
                TransactTypes.Network.TON,
                TransactTypes.Network.XLM,
                TransactTypes.Network.XRP,
            ].includes(network.value);
        });
        const onInputWalletEntered = (value) => {
            buttonDisabled.value = checkIfButtonIsDisabled(value, walletAddress.value.tag);
        };
        const onInputTagEntered = (value) => {
            buttonDisabled.value = checkIfButtonIsDisabled(walletAddress.value.address, value);
        };
        const goBack = () => {
            router.push('/transact');
            currentStep.value = TransactTypes.TransactSteps.BUY_3;
        };
        return {
            selectedCrypto,
            selectedCryptoAmount,
            numberFormat,
            onInputWalletEntered,
            onInputTagEntered,
            walletAddress,
            getBankDetails: transactionsStore.getBankDetails,
            buttonDisabled,
            BuyTypes: TransactTypes,
            KycTypes,
            kycStatus,
            goBack,
            error,
            loading,
            transactionError,
            transactionLoading,
            isLoggedIn,
            showHome,
            network,
            interceptorLoading,
            interceptorError,
            walletTagRequired,
            tagText,
            rules,
            shouldTriggerTravelRule,
            travelRuleInfo,
            travelRuleConfig,
            travelRuleFields,
        };
    },
});
